export const formatCurrency = (
	amount: string | number,
	currencyCode: string = 'USD',
): string => {
	const numericAmount =
		typeof amount === 'string' ? parseFloat(amount) : amount;

	if (isNaN(numericAmount)) {
		return '';
	}

	return new Intl.NumberFormat('en-US', {
		style: 'currency',
		currency: currencyCode || 'USD',
		minimumFractionDigits: 0,
		maximumFractionDigits: 3,
	}).format(numericAmount);
};

// Per-item card-brand fees can be fractions of a cent (e.g. $0.0195, $0.0018,
// $0.0047). formatCurrency caps at 3 decimals, which rounds those away
// ($0.0195 -> $0.02). For the per-item-fee column Clay needs the value shown at
// source precision, so allow up to 4 decimals (min 2; Intl trims trailing zeros
// beyond the minimum, so $0.30 stays $0.30 and $0.025 stays $0.025). Use this
// ONLY for per-item fees — dollar totals/volumes keep formatCurrency.
export const formatPreciseFee = (
	amount: string | number,
	currencyCode: string = 'USD',
): string => {
	const numericAmount =
		typeof amount === 'string' ? parseFloat(amount) : amount;

	if (isNaN(numericAmount)) {
		return '';
	}

	return new Intl.NumberFormat('en-US', {
		style: 'currency',
		currency: currencyCode || 'USD',
		minimumFractionDigits: 2,
		maximumFractionDigits: 4,
	}).format(numericAmount);
};

export const convertUtcToLocalTime = (
	utcString: string,
	locale = navigator.language || 'en-US',
	options: Intl.DateTimeFormatOptions = {},
) => {
	if (!utcString) return '';
	const isoString = utcString.replace(' ', 'T');

	const utcDate = new Date(isoString + 'Z');

	const defaultOptions = {
		year: 'numeric',
		month: 'short',
		day: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
		second: '2-digit',
		timeZoneName: 'short',
	} as Intl.DateTimeFormatOptions;

	return utcDate.toLocaleString(locale, { ...defaultOptions, ...options });
};

export const formatRatePlanType = (
	ratePlanType: string | undefined,
): string => {
	if (!ratePlanType) return 'N/A';

	switch (ratePlanType) {
		case 'IC_PLUS':
			return 'Interchange-Plus';
		case 'TIERED':
			return 'Tiered';
		case 'FLAT':
			return 'Flat';
		case 'FLAT_TIERED':
			return 'Flat Rate (Transactional)';
		case 'ALL':
			return 'Tiered & Interchange-Plus';
		default:
			return 'N/A';
	}
};

export const formatRatePlanTypes = (
	ratePlanTypes: string[] | undefined,
): string => {
	if (!ratePlanTypes || ratePlanTypes.length === 0) return 'N/A';

	const formattedTypes = ratePlanTypes.map((type) => {
		switch (type) {
			case 'IC_PLUS':
				return 'Interchange-Plus';
			case 'TIERED':
				return 'Tiered';
			case 'FLAT_TIERED':
				return 'Flat Rate (Transactional)';
			default:
				return type;
		}
	});

	return formattedTypes.join(' & ');
};

export const formatDuration = (duration: number): string => {
	if (duration === 0) return '0s';

	const days = Math.floor(duration / 86400);
	const hours = Math.floor((duration % 86400) / 3600);
	const minutes = Math.floor((duration % 3600) / 60);
	const seconds = Math.floor(duration % 60);

	if (days > 0) return `${days}d ${hours}h ${minutes}m ${seconds}s`;
	if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
	if (minutes > 0) return `${minutes}m ${seconds}s`;
	if (seconds > 0) return `${seconds}s`;

	return `${seconds}s`;
};

/**
 * Ensure timestamp is treated as UTC by appending 'Z' if it lacks a timezone specifier.
 * Handles formats like:
 * - 2025-11-19T17:47:43.556465 → 2025-11-19T17:47:43.556465Z
 * - 2025-11-19T17:47:43.556465Z → unchanged
 * - 2025-11-19T17:47:43+05:30 → unchanged
 * - 2025-11-19T17:47:43-05:00 → unchanged
 */
const ensureUtcTimestamp = (timestamp: string): string => {
	const timezoneRegex = /(Z|[+-]\d{2}(:?\d{2})?)$/;
	return timezoneRegex.test(timestamp) ? timestamp : `${timestamp}Z`;
};

/**
 * Format a timestamp as a relative time string (e.g., "2h ago", "just now")
 */
export const formatRelativeTime = (timestamp: string): string => {
	const now = new Date();
	const utcTimestamp = ensureUtcTimestamp(timestamp);
	const then = new Date(utcTimestamp);
	const diffMs = now.getTime() - then.getTime();
	const diffMins = Math.floor(diffMs / 60000);
	const diffHours = Math.floor(diffMs / 3600000);
	const diffDays = Math.floor(diffMs / 86400000);

	if (diffMins < 1) return 'just now';
	if (diffMins < 60) return `${diffMins}m ago`;
	if (diffHours < 24) return `${diffHours}h ago`;
	return `${diffDays}d ago`;
};

/**
 * Format a timestamp as a full date/time string (e.g., "Nov 19, 2025, 10:30 AM")
 */
export const formatTimestamp = (timestamp: string): string => {
	const utcTimestamp = ensureUtcTimestamp(timestamp);

	return new Date(utcTimestamp).toLocaleString('en-US', {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
		hour: 'numeric',
		minute: '2-digit',
		hour12: true,
	});
};

/**
 * Check if a user has any of the specified roles.
 *
 * @param userRoles - The user's roles (can be array or undefined/null)
 * @param requiredRoles - Array of role names to check for
 * @returns true if user has at least one of the required roles, false otherwise
 */
export const hasAnyRole = (
	userRoles: string[] | undefined | null,
	requiredRoles: string[],
): boolean => {
	if (!userRoles || !Array.isArray(userRoles)) {
		return false;
	}
	return requiredRoles.some((role) => userRoles.includes(role));
};

/**
 * Check if a user has a specific role.
 *
 * @param userRoles - The user's roles (can be array or undefined/null)
 * @param role - The role name to check for
 * @returns true if user has the role, false otherwise
 */
export const hasRole = (
	userRoles: string[] | undefined | null,
	role: string,
): boolean => {
	if (!userRoles || !Array.isArray(userRoles)) {
		return false;
	}
	return userRoles.includes(role);
};
